import { useEffect } from "react";
import { CANONICAL_ORIGIN } from "@/data/site";

// Lightweight SEO manager (no react-helmet dep).
// Updates <title>, meta tags, canonical, OG/Twitter, and injects
// per-page JSON-LD graph nodes into the head.
//
// props:
//  - title: <title> and og:title
//  - description: meta + og description
//  - path: canonical path e.g. "/leadership/biography"
//  - image: absolute image URL for og/twitter
//  - jsonLd: array or single JSON-LD object(s) unique to this page

const setMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs.match).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v));
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const PAGE_LD_ID = "__page-jsonld";

export default function Seo({ title, description, path = "/leadership", image, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;

    const canonical = `${CANONICAL_ORIGIN}${path}`;

    if (description) {
      setMeta('meta[name="description"]', {
        match: { name: "description" },
        set: { name: "description", content: description },
      });
      setMeta('meta[property="og:description"]', {
        match: { property: "og:description" },
        set: { property: "og:description", content: description },
      });
      setMeta('meta[name="twitter:description"]', {
        match: { name: "twitter:description" },
        set: { name: "twitter:description", content: description },
      });
    }

    if (title) {
      setMeta('meta[property="og:title"]', {
        match: { property: "og:title" },
        set: { property: "og:title", content: title },
      });
      setMeta('meta[name="twitter:title"]', {
        match: { name: "twitter:title" },
        set: { name: "twitter:title", content: title },
      });
    }

    setMeta('meta[property="og:url"]', {
      match: { property: "og:url" },
      set: { property: "og:url", content: canonical },
    });

    if (image) {
      setMeta('meta[property="og:image"]', {
        match: { property: "og:image" },
        set: { property: "og:image", content: image },
      });
      setMeta('meta[name="twitter:image"]', {
        match: { name: "twitter:image" },
        set: { name: "twitter:image", content: image },
      });
    }

    setLink("canonical", canonical);

    // JSON-LD
    const prev = document.getElementById(PAGE_LD_ID);
    if (prev) prev.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = PAGE_LD_ID;
      s.text = JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd]);
      document.head.appendChild(s);
    }

    // Scroll to top on route change
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [title, description, path, image, jsonLd]);

  return null;
}
